import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Copy, Star, MessageSquareQuote, Info, Eye, MailQuestion, HeartHandshake,
  Sparkles, Loader2, CheckCircle2, Download, RotateCcw, Scale, Lightbulb,
} from "lucide-react";
import { toast } from "sonner";
import { ManualGbpChecklist } from "@/components/ManualGbpChecklist";
import { ConsultingControl } from "@/components/admin/ConsultingControl";

const WIDE_SIGNALS = [
  { key: "geo_visibility", label: "KI-Sichtbarkeit" },
  { key: "pagespeed", label: "PageSpeed" },
  { key: "organic_ranks", label: "Organik Sıra" },
  { key: "backlinks", label: "Backlink" },
];
// "skipped" = the business has no own domain to measure (e.g. its
// "website" is just a Facebook page) — not a failure, just not applicable.
const signalState = (sig) => {
  if (sig == null) return "none";
  if (Array.isArray(sig)) return "ok";
  if (sig.skipped) return "skip";
  if (sig.error) return "err";
  return "ok";
};

const MONTHS = [
  { key: "month_1", label: "1. Ay" },
  { key: "month_2", label: "2. Ay" },
  { key: "month_3", label: "3. Ay" },
];

const SEVERITY = {
  critical: { label: "Kritik", cls: "bg-red-100 text-red-700 border-red-200" },
  high: { label: "Yüksek", cls: "bg-orange-100 text-orange-700 border-orange-200" },
  medium: { label: "Orta", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  low: { label: "Düşük", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

// Matches backend/server.py's SUPPORT_SERVICES — admin-facing Turkish labels
// for the paid report's "Destek" tab request.
const SUPPORT_SERVICE_LABELS = {
  consulting_only: "Sadece danışmanlık",
  full_service: "Tüm eksikliklerin bizim tarafımızdan giderilmesi",
  website_rebuild: "Web sitesi yeniden oluşturulması",
  website_upgrade: "Mevcut web sitesinin güncellenmesi",
  google_optimization: "Google optimizasyonu",
  ai_optimization: "Yapay zekâ optimizasyonu",
};

// Round-trips {text, priority} actions through a plain textarea as
// "[Öncelik] metin" lines, so the admin can still free-edit the whole plan
// as text — no rich per-field UI needed just for this. Actions predating
// 2026-09-14 are bare strings (no priority); those come back as "orta" if
// re-saved, which is a reasonable default, not data loss (they had no
// priority to preserve).
const actionsToText = (actions) =>
  (actions || [])
    .map((a) => (typeof a === "string" ? a : `[${SEVERITY[a.priority]?.label || a.priority}] ${a.text}`))
    .join("\n");

const textToActions = (text) =>
  text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const match = line.match(/^\[([^\]]+)\]\s*(.*)$/);
      if (!match) return { text: line, priority: "medium" };
      const priority = Object.keys(SEVERITY).find((k) => SEVERITY[k].label === match[1]) || "medium";
      return { text: match[2], priority };
    });

// Weights mirror backend/audit/scoring.py PILLAR_WEIGHTS — keep them in sync.
const PILLARS = {
  web: "Web & Teknik",
  onpage: "On-Page SEO",
  local: "Local SEO",
  gbp: "Google İşletme",
  reviews: "Yorumlar",
  ai: "AI Görünürlük",
};
const PILLAR_MAX = { web: 18, onpage: 20, local: 13, gbp: 20, reviews: 17, ai: 12 };

export const AuditDetailDialog = ({
  business, benchmark, open, onOpenChange,
  onUpdateManualGbpReview, onAnalyzeGbpText, onSuggestPhotoCategories,
  onGatherWideReport, onGenerateWideReport, onUpdateWideReportNarrative, onApproveWideReport,
  onGatherContentOpportunities, onGenerateContentRecommendations,
  onGrantConsulting,
}) => {
  const [gathering, setGathering] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [gatheringContent, setGatheringContent] = useState(false);
  const [generatingContent, setGeneratingContent] = useState(false);

  // Reset local edits only when the dialog switches to a different
  // business — not on every parent re-render for the same one (an
  // unrelated action elsewhere in the app must not clobber in-progress
  // edits here). A fresh generate/gather explicitly overwrites this via
  // setDraft in its own handler below.
  useEffect(() => {
    setDraft(business?.wide_report?.narrative || null);
  }, [business?.place_id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!business) return null;

  const pillars = business.score_pillars || {};
  const max = PILLAR_MAX;
  const findings = business.audit_findings || [];
  const ra = business.review_analysis;
  const ps = business.photo_stats;
  const wideReport = business.wide_report;
  const wideData = wideReport?.data;
  const contentOpportunities = wideData?.content_opportunities;
  const contentRecs = wideReport?.content_recs;
  const isApproved = wideReport?.status === "approved";
  const legal = business.legal;
  const legalIssues = legal?.issues || [];

  const runGather = async () => {
    setGathering(true);
    try {
      await onGatherWideReport(business);
      toast.success("Ek veriler toplandı");
    } catch (e) {
      toast.error("Veri toplama başarısız");
    } finally {
      setGathering(false);
    }
  };

  const runGenerate = async () => {
    setGenerating(true);
    try {
      const wr = await onGenerateWideReport(business);
      setDraft(wr.narrative);
      toast.success("Taslak oluşturuldu");
    } catch (e) {
      toast.error("Taslak oluşturulamadı", {
        description: e?.response?.data?.detail || "Anthropic isteği başarısız oldu.",
      });
    } finally {
      setGenerating(false);
    }
  };

  const runGatherContent = async () => {
    setGatheringContent(true);
    try {
      await onGatherContentOpportunities(business);
      toast.success("İçerik fırsatları toplandı");
    } catch (e) {
      toast.error("Toplama başarısız");
    } finally {
      setGatheringContent(false);
    }
  };

  const runGenerateContent = async () => {
    setGeneratingContent(true);
    try {
      await onGenerateContentRecommendations(business);
      toast.success("İçerik önerileri oluşturuldu");
    } catch (e) {
      toast.error("Öneriler oluşturulamadı", {
        description: e?.response?.data?.detail || "Anthropic isteği başarısız oldu.",
      });
    } finally {
      setGeneratingContent(false);
    }
  };

  const runSave = async () => {
    setSaving(true);
    try {
      await onUpdateWideReportNarrative(business, draft);
      toast.success("Taslak kaydedildi");
    } catch (e) {
      toast.error("Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const runApprove = async () => {
    setApproving(true);
    try {
      try {
        await onApproveWideReport(business);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        if (e?.response?.status === 400 && detail?.code === "quality_gate") {
          const proceed = window.confirm(
            `${detail.blockers.join("\n")}\n\nBu haliyle müşteriye gidecek. Yine de onaylansın mı?`
          );
          if (!proceed) return;
          await onApproveWideReport(business, true);
        } else {
          throw e;
        }
      }
      toast.success("Rapor onaylandı — artık halka açık rapor sayfasında görünecek");
    } catch (e) {
      toast.error("Onaylanamadı");
    } finally {
      setApproving(false);
    }
  };

  const updateMonth = (key, field, value) =>
    setDraft((d) => ({ ...d, [key]: { ...d[key], [field]: value } }));

  const copySummary = () => {
    const lines = [
      `${business.name} — Dijital Varlık Puanı: ${business.digital_score}/100`,
      benchmark?.score_median
        ? `Bölgedeki rakip medyanı: ${benchmark.score_median}/100`
        : null,
      "",
      ...findings.slice(0, 5).map((f) => `• ${f.title}: ${f.evidence}`),
    ].filter(Boolean);
    navigator.clipboard?.writeText(lines.join("\n"));
    toast.success("Özet kopyalandı");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="audit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {business.name} · {business.digital_score}/100
          </DialogTitle>
          <DialogDescription>
            {benchmark?.score_median != null && (
              <>
                Aynı aramadaki {benchmark.cohort_size} firmanın medyanı{" "}
                <strong>{benchmark.score_median}/100</strong>.{" "}
              </>
            )}
            Düşük puan = zayıf dijital varlık = güçlü fırsat.
          </DialogDescription>
        </DialogHeader>

        {(business.report_views?.length > 0 || business.deep_report_request || business.support_request) && (
          <div className="space-y-2">
            {business.report_views?.length > 0 && (
              <div className="flex items-center gap-1.5 rounded-md bg-secondary/60 px-3 py-2 text-xs text-muted-foreground">
                <Eye className="h-3.5 w-3.5 shrink-0" />
                Rapor <strong>{business.report_views.length}</strong> kez görüntülendi
                {" · "}son: {new Date(business.report_views[business.report_views.length - 1].at).toLocaleString("tr-TR")}
              </div>
            )}
            {business.deep_report_request && (
              <div
                className="rounded-md border border-primary/40 bg-accent px-3 py-2 text-xs"
                data-testid="deep-report-request"
              >
                <div className="flex items-center gap-1.5 font-medium text-primary">
                  <MailQuestion className="h-3.5 w-3.5 shrink-0" />
                  Detaylı analiz talep etti
                  {" · "}{new Date(business.deep_report_request.at).toLocaleString("tr-TR")}
                </div>
                <div className="mt-1 space-y-0.5 text-foreground">
                  {business.deep_report_request.name && <div>Ad: {business.deep_report_request.name}</div>}
                  {business.deep_report_request.contact && <div>İletişim: {business.deep_report_request.contact}</div>}
                  {business.deep_report_request.message && <div>Mesaj: {business.deep_report_request.message}</div>}
                </div>
              </div>
            )}
            {business.support_request && (
              <div
                className="rounded-md border border-accent-foreground/30 bg-accent px-3 py-2 text-xs"
                data-testid="support-request"
              >
                <div className="flex items-center gap-1.5 font-medium text-accent-foreground">
                  <HeartHandshake className="h-3.5 w-3.5 shrink-0" />
                  Destek sekmesinden ek hizmet talebi
                  {" · "}{new Date(business.support_request.at).toLocaleString("tr-TR")}
                </div>
                <div className="mt-1 space-y-0.5 text-foreground">
                  <div>Ad: {business.support_request.name}</div>
                  <div>İletişim: {business.support_request.contact}</div>
                  <div>
                    İstenen hizmetler: {(business.support_request.services || [])
                      .map((s) => SUPPORT_SERVICE_LABELS[s] || s).join(", ")}
                  </div>
                  {business.support_request.message && <div>Mesaj: {business.support_request.message}</div>}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Pillars */}
        <div className="space-y-2">
          {Object.entries(PILLARS).map(([key, label]) => {
            const val = pillars[key] ?? 0;
            const pct = (val / max[key]) * 100;
            return (
              <div key={key} className="flex items-center gap-3">
                <span className="w-32 shrink-0 text-xs text-muted-foreground">{label}</span>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="w-12 shrink-0 text-right text-xs tabular-nums">
                  {val}/{max[key]}
                </span>
              </div>
            );
          })}
        </div>

        <ManualGbpChecklist
          business={business}
          onUpdate={onUpdateManualGbpReview}
          onAnalyzeText={onAnalyzeGbpText}
          onSuggestPhotos={onSuggestPhotoCategories}
        />

        {/* Findings */}
        <div className="mt-2">
          <h4 className="mb-2 text-sm font-semibold">Bulgular ({findings.length})</h4>
          <div className="space-y-2">
            {findings.map((f, i) => {
              const sev = SEVERITY[f.severity] || SEVERITY.low;
              return (
                <div key={i} className="rounded-lg border p-2.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className={`text-[10px] ${sev.cls}`}>
                      {sev.label}
                    </Badge>
                    <span className="text-sm font-medium">{f.title}</span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{f.evidence}</p>
                  <p className="mt-1 text-[11px] text-primary">→ {f.service}</p>
                </div>
              );
            })}
            {findings.length === 0 && (
              <p className="text-sm text-muted-foreground">
                Henüz tarama yapılmadı — "Dijital Tarama" butonunu çalıştırın.
              </p>
            )}
          </div>
        </div>

        {/* Showcase reviews */}
        {ra?.index != null && (
          <div className="mt-2">
            <h4 className="mb-1 flex items-center gap-1.5 text-sm font-semibold">
              <MessageSquareQuote className="h-4 w-4" />
              Vitrin Yorum Kalitesi: {ra.index}/100
            </h4>
            <p className="mb-2 flex items-start gap-1.5 rounded-md bg-secondary/60 p-2 text-[11px] text-muted-foreground">
              <Info className="mt-0.5 h-3 w-3 shrink-0" />
              Google her profil için yalnızca <strong className="mx-0.5">5 öne çıkan</strong>
              yorum veriyor ve bunlar en olumlu olanlar. Bu yüzden bu değer tüm
              yorumların ortalaması değil, <strong className="mx-0.5">müşterilere ilk gösterilen
              vitrinin</strong> kalitesidir.
            </p>
            <div className="mb-2 flex flex-wrap gap-1.5 text-[11px]">
              <span className="rounded-full border bg-card px-2 py-0.5">
                Şehir/semt geçen: <strong>{ra.with_locality}/{ra.sample_size}</strong>
              </span>
              <span className="rounded-full border bg-card px-2 py-0.5">
                Hizmet adı geçen: <strong>{ra.with_service}/{ra.sample_size}</strong>
              </span>
              <span className="rounded-full border bg-card px-2 py-0.5">
                Salt teşekkür: <strong>{ra.praise_only_count}</strong>
              </span>
              {ps?.total > 0 && (
                <span className="rounded-full border bg-card px-2 py-0.5">
                  Müşteri fotoğrafı: <strong>{ps.customer}/{ps.total}</strong>
                </span>
              )}
            </div>
            <div className="space-y-1.5">
              {(ra.items || []).map((it, i) => (
                <div key={i} className="rounded-md border p-2 text-xs">
                  <div className="mb-1 flex items-center gap-2">
                    <span className="inline-flex items-center gap-0.5 tabular-nums">
                      <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                      {it.rating}
                    </span>
                    <span className="font-medium">{it.score}/4</span>
                    <span className="text-muted-foreground">
                      {it.hits.length ? it.hits.join(" · ") : "içeriksiz"}
                    </span>
                    <span className="ml-auto text-[10px] text-muted-foreground">
                      {it.published}
                    </span>
                  </div>
                  <p className="text-muted-foreground">{it.excerpt}…</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Legal compliance — separate from the score (memory/PRD.md §12) */}
        {legalIssues.length > 0 && (
          <div className="mt-2">
            <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
              <Scale className="h-4 w-4" />
              Yasal Kontrol ({legalIssues.length})
              {legal?.platform_hosted && (
                <Badge variant="outline" className="text-[10px]">platform sayfası</Badge>
              )}
            </h4>
            <div className="space-y-2">
              {legalIssues.map((f, i) => {
                const sev = SEVERITY[f.severity] || SEVERITY.low;
                return (
                  <div key={i} className="rounded-lg border p-2.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className={`text-[10px] ${sev.cls}`}>
                        {sev.label}
                      </Badge>
                      <span className="text-sm font-medium">{f.title}</span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">{f.evidence}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Wide report: paid-tier draft, admin-reviewed before it can ever
            reach a business — see memory/PRD.md §5. Requires a base audit. */}
        {business.digital_score != null && (
          <div className="mt-3 rounded-lg border border-dashed p-3">
            <div className="mb-2 flex items-center justify-between">
              <h4 className="flex items-center gap-1.5 text-sm font-semibold">
                <Sparkles className="h-4 w-4" /> Geniş Rapor (Ödemeli)
              </h4>
              {isApproved && (
                <Badge className="gap-1 bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                  <CheckCircle2 className="h-3 w-3" /> Onaylandı
                </Badge>
              )}
            </div>

            {/* Signal status + gather trigger */}
            <div className="mb-2 flex flex-wrap items-center gap-1.5">
              {WIDE_SIGNALS.map(({ key, label }) => {
                const sig = wideData?.[key];
                const state = signalState(sig);
                const ICON = { ok: "✓", err: "✗", skip: "–", none: "–" }[state];
                return (
                  <span
                    key={key}
                    className={`rounded-full border px-2 py-0.5 text-[10px] ${
                      state === "ok"
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : state === "err"
                        ? "border-red-200 bg-red-50 text-red-600"
                        : "border-slate-200 bg-slate-50 text-slate-400"
                    }`}
                    title={state === "err" ? sig.error : state === "skip" ? sig.skipped : undefined}
                  >
                    {label} {ICON}
                  </span>
                );
              })}
              <Button
                variant="outline" size="sm" className="ml-auto h-7 text-xs"
                onClick={runGather} disabled={gathering}
              >
                {gathering ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                Ek Veri Topla
              </Button>
            </div>

            {/* Draft generate/edit/approve */}
            {!draft ? (
              <Button size="sm" onClick={runGenerate} disabled={generating} data-testid="generate-wide-report">
                {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Taslak Oluştur
              </Button>
            ) : (
              <div className="space-y-2.5">
                <Input
                  value={draft.headline || ""}
                  onChange={(e) => setDraft((d) => ({ ...d, headline: e.target.value }))}
                  placeholder="Başlık"
                  className="text-sm font-medium"
                  disabled={isApproved}
                />
                <Textarea
                  value={draft.summary || ""}
                  onChange={(e) => setDraft((d) => ({ ...d, summary: e.target.value }))}
                  placeholder="Özet"
                  rows={3}
                  className="text-xs"
                  disabled={isApproved}
                />
                {MONTHS.map(({ key, label }) => (
                  <div key={key} className="rounded-md border p-2">
                    <span className="text-[11px] font-semibold text-muted-foreground">{label}</span>
                    <Input
                      value={draft[key]?.theme || ""}
                      onChange={(e) => updateMonth(key, "theme", e.target.value)}
                      placeholder="Tema"
                      className="mt-1 text-xs"
                      disabled={isApproved}
                    />
                    <Textarea
                      value={actionsToText(draft[key]?.actions)}
                      onChange={(e) => updateMonth(key, "actions", textToActions(e.target.value))}
                      placeholder="Her satıra bir aksiyon, opsiyonel olarak [Kritik]/[Yüksek]/[Orta]/[Düşük] önekiyle"
                      rows={4}
                      className="mt-1 text-xs"
                      disabled={isApproved}
                    />
                  </div>
                ))}
                <div className="rounded-md border p-2">
                  <span className="text-[11px] font-semibold text-muted-foreground">Ek Öneriler (ranking'i doğrudan etkilemez)</span>
                  <Textarea
                    value={(draft.additional_notes || []).join("\n")}
                    onChange={(e) => setDraft((d) => ({ ...d, additional_notes: e.target.value.split("\n") }))}
                    placeholder="Her satıra bir öneri"
                    rows={3}
                    className="mt-1 text-xs"
                    disabled={isApproved}
                  />
                </div>
                {!isApproved && (
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={runSave} disabled={saving}>
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      Taslağı Kaydet
                    </Button>
                    <Button variant="outline" size="sm" onClick={runGenerate} disabled={generating}>
                      {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                      Yeniden Oluştur
                    </Button>
                    <Button
                      variant="default" size="sm" className="ml-auto bg-emerald-600 hover:bg-emerald-700"
                      onClick={runApprove} disabled={approving}
                    >
                      {approving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                      Onayla ve Yayınla
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Content opportunities (owner request 2026-10-07): sitemap
            topic-gap + competitor-directory signals, then Claude-written
            article/post ideas grounded in those signals + question_gaps.
            Own buttons (see the plan's Global Constraints for why this is
            not folded into the "Ek Veri Topla" button above); no separate
            approval step (K7) — approving the main wide report above also
            exposes this to the public report. */}
        {business.digital_score != null && business.website && (
          <div className="mt-3 rounded-lg border border-dashed p-3">
            <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
              <Lightbulb className="h-4 w-4" /> İçerik Fırsatları
            </h4>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline" size="sm" className="h-7 text-xs"
                onClick={runGatherContent} disabled={gatheringContent}
              >
                {gatheringContent ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                İçerik Fırsatlarını Topla
              </Button>
              <Button
                size="sm" className="h-7 text-xs"
                onClick={runGenerateContent} disabled={generatingContent || !contentOpportunities}
              >
                {generatingContent ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                İçerik Önerileri Oluştur
              </Button>
            </div>
            {contentRecs && (
              <div className="mt-2 space-y-1 rounded-md border bg-muted/30 p-2 text-xs">
                <p className="font-medium text-muted-foreground">
                  ✓ {contentRecs.articles?.length || 0} makale, {contentRecs.quick_posts?.length || 0} hızlı paylaşım — onaylamadan önce gözden geçirin:
                </p>
                {contentRecs.articles?.map((a, i) => (
                  <p key={`a${i}`}>• {a.title}</p>
                ))}
                {contentRecs.quick_posts?.map((q, i) => (
                  <p key={`q${i}`}>• {q.idea}</p>
                ))}
              </div>
            )}
          </div>
        )}

        {business.digital_score != null && onGrantConsulting && (
          <ConsultingControl
            consulting={business.consulting}
            onGrant={(source) => onGrantConsulting(business, source)}
          />
        )}

        <div className="mt-2 flex justify-end">
          <Button variant="secondary" size="sm" onClick={copySummary}>
            <Copy className="h-4 w-4" /> Özeti Kopyala
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
